import { useState } from 'react';
import { LoadingButton } from '@mui/lab';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Paper,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { keys, map } from 'lodash';
import { useSnackbar } from 'notistack';
import PropTypes from 'prop-types';
import { Form } from 'react-final-form';
import { useDispatch } from 'react-redux';

import cancelOrderReasons from 'common/constants/cancelOrderReasons';
import { validateMaxLength } from 'common/forms/formValidations';
import MarkdownField from 'common/forms/MarkdownField';
import SelectField from 'common/forms/SelectField';
import formatDrugLabel from 'common/utils/formatDrugLabel';
import { cancelFillLaterItem } from 'store/thunks/workQueueThunks';

import { formatScheduledDate } from '../utils/fillLaterDates';

const CancelFillLaterDialog = ({ open, onClose, item }) => {
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const [submitting, setSubmitting] = useState(false);

  const handleClose = (_, reason) => {
    if (reason === 'backdropClick' || submitting) return;
    onClose();
  };

  const handleConfirm = async ({ cancelReason, cancelNote }) => {
    setSubmitting(true);
    try {
      const result = await dispatch(
        cancelFillLaterItem({ fillLaterItemId: item.fillLaterItemId, cancelReason, cancelNote })
      );
      if (!result) return;

      enqueueSnackbar(
        `Fill later scheduled for ${formatScheduledDate(item.scheduledFor)} cancelled`,
        { variant: 'success' }
      );
      onClose();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth='sm'
      fullWidth
      aria-labelledby='CancelFillLaterDialog-title'
    >
      <DialogTitle id='CancelFillLaterDialog-title' sx={{ pb: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography sx={{ fontSize: '1.15rem', fontWeight: 600 }}>
            Cancel this Fill Later?
          </Typography>
          <IconButton size='small' onClick={onClose} disabled={submitting}>
            <CloseIcon fontSize='small' />
          </IconButton>
        </Box>
      </DialogTitle>
      <Form
        onSubmit={handleConfirm}
        render={({ handleSubmit, invalid }) => (
          <form noValidate onSubmit={handleSubmit}>
            <DialogContent sx={{ pt: 0 }}>
              <Paper
                elevation={0}
                sx={{
                  bgcolor: 'grey.50',
                  border: '1px solid #e0e0e0',
                  borderRadius: 1,
                  p: 2,
                  mb: 2.5,
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    gap: 2,
                    mb: 1,
                  }}
                >
                  <Typography variant='h6' sx={{ fontWeight: 700, fontSize: '1.05rem' }}>
                    {formatDrugLabel(item)}
                  </Typography>
                  <Typography variant='body2' sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>
                    Qty {item.splitQuantity}
                  </Typography>
                </Box>

                <Typography variant='body2' color='text.secondary'>
                  <Box component='span' sx={{ fontWeight: 600, color: 'text.primary' }}>
                    Patient:
                  </Box>{' '}
                  {item.patientName}
                </Typography>
                <Typography variant='body2' color='text.secondary'>
                  <Box component='span' sx={{ fontWeight: 600, color: 'text.primary' }}>
                    Scheduled for:
                  </Box>{' '}
                  {formatScheduledDate(item.scheduledFor)}
                </Typography>
                <Typography variant='body2' color='text.secondary'>
                  <Box component='span' sx={{ fontWeight: 600, color: 'text.primary' }}>
                    Source order:
                  </Box>{' '}
                  {item.referenceOrderId}
                </Typography>
              </Paper>

              <SelectField
                id='CancelFillLaterDialog-cancelReason-dropdown'
                label='Reason for cancelling'
                name='cancelReason'
                required
              >
                {map(keys(cancelOrderReasons), (key) => (
                  <MenuItem value={key} key={key}>
                    {cancelOrderReasons[key]}
                  </MenuItem>
                ))}
              </SelectField>

              <MarkdownField
                name='cancelNote'
                id='CancelFillLaterDialog-cancelNote-input'
                height='150px'
                validations={[
                  validateMaxLength('The note is too long. Please make it shorter', 65435),
                ]}
                label='Cancel Note'
                required
              />

              <Alert
                severity='warning'
                sx={{
                  mt: 1,
                  bgcolor: '#fff3e0',
                  color: '#ff9800',
                  '& .MuiAlert-icon': { color: '#ff9800' },
                }}
              >
                {item.isOrderPrecreated
                  ? `The line item this fill is holding is cancelled with it, and its order once nothing else is left on it. Qty ${item.splitQuantity} is not added back to the prescription.`
                  : `Only the schedule is cancelled. Qty ${item.splitQuantity} is not added back to the prescription, and the original order is not affected.`}
              </Alert>
            </DialogContent>
            <DialogActions>
              <Button variant='outlined' color='secondary' onClick={onClose} disabled={submitting}>
                Keep it
              </Button>
              <LoadingButton
                type='submit'
                variant='contained'
                color='primary'
                loading={submitting}
                disabled={invalid}
                id={`CancelFillLaterDialog-${item.fillLaterItemId}-confirm`}
              >
                Cancel Fill Later
              </LoadingButton>
            </DialogActions>
          </form>
        )}
      />
    </Dialog>
  );
};

CancelFillLaterDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  item: PropTypes.shape({
    fillLaterItemId: PropTypes.string.isRequired,
    referenceOrderId: PropTypes.string,
    patientName: PropTypes.string,
    drugName: PropTypes.string,
    strength: PropTypes.string,
    splitQuantity: PropTypes.string,
    scheduledFor: PropTypes.string,
    status: PropTypes.string,
    isOrderPrecreated: PropTypes.bool,
  }).isRequired,
};

export default CancelFillLaterDialog;
